import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  Form, Row, Col, ListGroup, Spinner,
} from 'react-bootstrap';
import _ from 'lodash';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_ACCESS_TOKEN } from '../../config/config';
import { fetchAddressSuggestions } from '../../Services/GeoService';
import { createGeoJSONCircle, DEFAULT_CENTER } from './geoUtils';

import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const PIN_COLORS = {
  origin: '#28a745',
  destination: '#dc3545',
};

const buildPinElement = (color) => {
  const el = document.createElement('div');
  el.innerHTML = `<i class="fa fa-map-marker-alt" style="color: ${color}; font-size: 28px; text-shadow: 0 1px 2px rgba(0,0,0,0.3);"></i>`;
  el.style.cursor = 'pointer';
  return el;
};

const hasCoords = (ep) => ep && Number.isFinite(Number(ep.latitude))
  && Number.isFinite(Number(ep.longitude));

/*
 * Controlled origin/destination editor for two RouteEndpoint values that
 * share a single Mapbox map. Each endpoint is { address, latitude, longitude,
 * match_radius_km }. Pins are draggable; a connecting line + radius circles
 * are rendered when both endpoints have coordinates.
 *
 * Active=false skips mounting the map entirely (useful inside collapsed UI).
 */
function RoutePairGeoEditor({
  origin,
  destination,
  onOriginChange,
  onDestinationChange,
  active = true,
  mapHeight = '400px',
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef({ origin: null, destination: null });

  // Refs for handlers to access latest props without forcing remount.
  const originRef = useRef(origin);
  const destinationRef = useRef(destination);
  const onOriginChangeRef = useRef(onOriginChange);
  const onDestinationChangeRef = useRef(onDestinationChange);
  originRef.current = origin;
  destinationRef.current = destination;
  onOriginChangeRef.current = onOriginChange;
  onDestinationChangeRef.current = onDestinationChange;

  const renderCircle = useCallback((endpointKey, endpoint) => {
    if (!map.current) return;

    const sourceId = `${endpointKey}-radius`;
    const layerId = `${endpointKey}-radius-fill`;
    const outlineId = `${endpointKey}-radius-outline`;
    const color = PIN_COLORS[endpointKey];

    const doRender = () => {
      if (!map.current) return;
      try {
        if (map.current.getLayer(outlineId)) map.current.removeLayer(outlineId);
        if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
        if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);
      } catch (e) {
        // ignore
      }

      if (!hasCoords(endpoint)) return;
      const radius = Number(endpoint.match_radius_km) || 2;
      const geo = createGeoJSONCircle(
        [Number(endpoint.longitude), Number(endpoint.latitude)],
        radius,
      );

      try {
        map.current.addSource(sourceId, { type: 'geojson', data: geo });
        map.current.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          paint: { 'fill-color': color, 'fill-opacity': 0.18 },
        });
        map.current.addLayer({
          id: outlineId,
          type: 'line',
          source: sourceId,
          paint: { 'line-color': color, 'line-width': 2 },
        });
      } catch (e) {
        setTimeout(doRender, 100);
      }
    };

    if (map.current.isStyleLoaded()) doRender();
    else map.current.once('idle', doRender);
  }, []);

  const renderConnector = useCallback(() => {
    if (!map.current) return;
    const o = originRef.current;
    const d = destinationRef.current;
    const sourceId = 'route-connector';
    const layerId = 'route-connector-line';

    const doRender = () => {
      if (!map.current) return;
      try {
        if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
        if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);
      } catch (e) {
        // ignore
      }

      if (!hasCoords(o) || !hasCoords(d)) return;

      const geo = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [Number(o.longitude), Number(o.latitude)],
            [Number(d.longitude), Number(d.latitude)],
          ],
        },
      };
      try {
        map.current.addSource(sourceId, { type: 'geojson', data: geo });
        map.current.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#0d6efd',
            'line-width': 3,
            'line-dasharray': [2, 2],
          },
        });
      } catch (e) {
        setTimeout(doRender, 100);
      }
    };

    if (map.current.isStyleLoaded()) doRender();
    else map.current.once('idle', doRender);
  }, []);

  // (Re)create a draggable pin for an endpoint. Falls back to the default
  // center if the endpoint has no coords yet but we still want it visible.
  const placePin = useCallback((endpointKey, endpoint) => {
    if (!map.current) return;
    if (markers.current[endpointKey]) {
      markers.current[endpointKey].remove();
      markers.current[endpointKey] = null;
    }
    if (!hasCoords(endpoint)) return;

    const el = buildPinElement(PIN_COLORS[endpointKey]);
    const pin = new mapboxgl.Marker({ element: el, draggable: true })
      .setLngLat([Number(endpoint.longitude), Number(endpoint.latitude)])
      .addTo(map.current);

    pin.on('dragend', () => {
      const lngLat = pin.getLngLat();
      const next = {
        ...(endpointKey === 'origin' ? originRef.current : destinationRef.current),
        latitude: lngLat.lat,
        longitude: lngLat.lng,
      };
      if (endpointKey === 'origin') onOriginChangeRef.current(next);
      else onDestinationChangeRef.current(next);
    });

    markers.current[endpointKey] = pin;
  }, []);

  // Map mount/teardown.
  useEffect(() => {
    if (!active || map.current) return undefined;

    const o = originRef.current;
    const d = destinationRef.current;
    let center;
    if (hasCoords(o) && hasCoords(d)) {
      center = {
        latitude: (Number(o.latitude) + Number(d.latitude)) / 2,
        longitude: (Number(o.longitude) + Number(d.longitude)) / 2,
      };
    } else if (hasCoords(o)) {
      center = { latitude: Number(o.latitude), longitude: Number(o.longitude) };
    } else if (hasCoords(d)) {
      center = { latitude: Number(d.latitude), longitude: Number(d.longitude) };
    } else {
      center = DEFAULT_CENTER;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [center.longitude, center.latitude],
      zoom: 8,
    });
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      placePin('origin', originRef.current);
      placePin('destination', destinationRef.current);
      renderCircle('origin', originRef.current);
      renderCircle('destination', destinationRef.current);
      renderConnector();

      // Fit bounds when both endpoints have coords.
      if (hasCoords(originRef.current) && hasCoords(destinationRef.current)) {
        const bounds = new mapboxgl.LngLatBounds(
          [Number(originRef.current.longitude), Number(originRef.current.latitude)],
          [Number(destinationRef.current.longitude), Number(destinationRef.current.latitude)],
        );
        map.current.fitBounds(bounds, { padding: 60 });
      }
    });

    // Click on empty map to set the next missing endpoint. Falls back to
    // moving the origin pin when both already exist.
    map.current.on('click', (e) => {
      const oNow = originRef.current;
      const dNow = destinationRef.current;
      const coord = { latitude: e.lngLat.lat, longitude: e.lngLat.lng };
      if (!hasCoords(oNow)) {
        onOriginChangeRef.current({ ...(oNow || {}), ...coord });
      } else if (!hasCoords(dNow)) {
        onDestinationChangeRef.current({ ...(dNow || {}), ...coord });
      }
    });

    return () => {
      ['origin', 'destination'].forEach((k) => {
        if (markers.current[k]) {
          markers.current[k].remove();
          markers.current[k] = null;
        }
      });
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [active, placePin, renderCircle, renderConnector]);

  // Sync pins + circles + connector when props change.
  useEffect(() => {
    if (!map.current) return;
    placePin('origin', origin);
    renderCircle('origin', origin);
    renderConnector();
  }, [
    origin?.latitude,
    origin?.longitude,
    origin?.match_radius_km,
    placePin,
    renderCircle,
    renderConnector,
  ]);

  useEffect(() => {
    if (!map.current) return;
    placePin('destination', destination);
    renderCircle('destination', destination);
    renderConnector();
  }, [
    destination?.latitude,
    destination?.longitude,
    destination?.match_radius_km,
    placePin,
    renderCircle,
    renderConnector,
  ]);

  // --- search (shared across both endpoints) ---------------------------------
  const [searchTarget, setSearchTarget] = useState('origin');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);

  const debouncedSearch = useCallback(
    _.debounce(async (query) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      setSearchLoading(true);
      try {
        const results = await fetchAddressSuggestions(query);
        setSearchResults(results || []);
        setShowSearchResults(true);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [],
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleSearchSelect = (result) => {
    const coords = {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
    };
    const patch = { ...coords, address: result.displayName || '' };

    if (searchTarget === 'origin') {
      onOriginChange({ ...(origin || {}), ...patch });
    } else {
      onDestinationChange({ ...(destination || {}), ...patch });
    }

    if (map.current) {
      map.current.flyTo({ center: [coords.longitude, coords.latitude], zoom: 12 });
    }
    setSearchQuery(result.displayName || '');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderEndpointFields = (key, label, value, onChange) => {
    const color = PIN_COLORS[key];
    return (
      <div className="mb-3">
        <h6 style={{ color }} className="mb-2">
          <i className="fa fa-map-marker-alt me-2" />
          {label}
        </h6>
        <Form.Group className="mb-2">
          <Form.Label className="small">Address</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            value={value?.address || ''}
            onChange={(e) => onChange({ ...(value || {}), address: e.target.value })}
            placeholder="Free-text address"
          />
        </Form.Group>
        <Row>
          <Col xs={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Latitude</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                step="0.0001"
                value={value?.latitude ?? ''}
                onChange={(e) => onChange({ ...(value || {}), latitude: Number(e.target.value) })}
              />
            </Form.Group>
          </Col>
          <Col xs={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Longitude</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                step="0.0001"
                value={value?.longitude ?? ''}
                onChange={(e) => onChange({ ...(value || {}), longitude: Number(e.target.value) })}
              />
            </Form.Group>
          </Col>
          <Col xs={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Match radius (km)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                min={0.1}
                step="0.1"
                value={value?.match_radius_km ?? 2}
                onChange={(e) => onChange({
                  ...(value || {}),
                  match_radius_km: Number(e.target.value),
                })}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <Row>
      <Col md={6}>
        {renderEndpointFields('origin', 'Origin', origin, onOriginChange)}
        {renderEndpointFields('destination', 'Destination', destination, onDestinationChange)}
      </Col>
      <Col md={6}>
        <div className="mb-3 position-relative" ref={searchRef}>
          <Form.Group>
            <Form.Label>
              <i className="fa fa-search me-2" />
              Search address — sets the
              {' '}
              <Form.Select
                size="sm"
                value={searchTarget}
                onChange={(e) => setSearchTarget(e.target.value)}
                style={{ display: 'inline-block', width: 'auto' }}
              >
                <option value="origin">origin</option>
                <option value="destination">destination</option>
              </Form.Select>
            </Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search for an address or place..."
                onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              />
              {searchLoading && (
                <div
                  className="position-absolute"
                  style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  <Spinner size="sm" animation="border" variant="secondary" />
                </div>
              )}
            </div>
          </Form.Group>

          {showSearchResults && searchResults.length > 0 && (
            <ListGroup
              className="position-absolute w-100 shadow"
              style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
            >
              {searchResults.map((result) => (
                <ListGroup.Item
                  key={`${result.lat}-${result.lon}`}
                  action
                  onClick={() => handleSearchSelect(result)}
                  className="py-2"
                >
                  <small>{result.displayName}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>

        <div
          ref={mapContainer}
          style={{ height: mapHeight, borderRadius: '8px' }}
        />
        <Form.Text className="text-muted">
          <i className="fa fa-arrows-alt me-1" />
          Drag pins to fine-tune. Click an empty area to drop the next missing endpoint.
        </Form.Text>
      </Col>
    </Row>
  );
}

export default RoutePairGeoEditor;
